/**
 * Reusable compose box component with file attachments (Preact port).
 *
 * Single unified input container with an attachment button (menu: Files,
 * Images, Camera), a vertically centered textarea, and a square send button.
 * Files can also be dragged and dropped onto the container.
 *
 * Attachments are uploaded to the workspace filesystem in the background;
 * the send button is disabled until every attachment finishes uploading.
 * onSubmit is called with (text, fileIDs) once all uploads complete.
 */
import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { uploadFile } from '../lib/api.js';
import { PaperclipIcon, SendIcon } from './Icons.jsx';

export function Compose({ onSubmit, placeholder = 'Ask anything...', autoFocus = false }) {
  const [text, setText] = useState('');
  const [pending, setPending] = useState([]); // { id, file, name, status, error }
  const [uploading, setUploading] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const textRef = useRef(null);
  const filesInputRef = useRef(null);
  const imagesInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && textRef.current) textRef.current.focus();
  }, [autoFocus]);

  const addFiles = useCallback(
    (files) => {
      if (!files || !files.length) return;
      const targets = Array.from(files).map((file) => ({
        path: null,
        file,
        name: file.name || 'file',
        status: 'uploading',
        error: null,
      }));
      setPending((prev) => [...prev, ...targets]);
      setUploading((n) => n + targets.length);
      targets.forEach((p) => {
        uploadFile(p.file)
          .then((info) => {
            setPending((prev) =>
              prev.map((q) => (q === p ? { ...q, path: info.path, status: 'done' } : q))
            );
          })
          .catch((err) => {
            setPending((prev) =>
              prev.map((q) => (q === p ? { ...q, status: 'error', error: err.message } : q))
            );
          })
          .finally(() => setUploading((n) => n - 1));
      });
    },
    []
  );

  const submit = () => {
    if (uploading > 0) return;
    const paths = pending.filter((p) => p.status === 'done').map((p) => p.path);
    const trimmed = text.trim();
    if (!trimmed && !paths.length) return;
    if (onSubmit) onSubmit(trimmed, paths);
    setText('');
    setPending([]);
  };

  return (
    <div
      className={`compose relative flex flex-col gap-1 border border-base-300 rounded-xl bg-base-100 px-3 py-2 max-w-3xl w-full mx-auto focus-within:border-primary transition-colors ${
        dragOver ? 'border-primary' : ''
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer) addFiles(e.dataTransfer.files);
      }}
    >
      {/* Hidden file inputs for the attachment menu */}
      <input
        ref={filesInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.currentTarget.files);
          e.currentTarget.value = '';
        }}
      />
      <input
        ref={imagesInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.currentTarget.files);
          e.currentTarget.value = '';
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          addFiles(e.currentTarget.files);
          e.currentTarget.value = '';
        }}
      />

      {pending.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {pending.map((p) => (
            <span
              key={p.name + p.id}
              className={`inline-flex items-center gap-1.5 rounded-lg bg-base-200 px-2 py-0.5 text-xs ${
                p.status === 'error' ? 'text-error' : 'text-base-content/80'
              }`}
            >
              {p.name}
              {p.status === 'uploading' && '…'}
              {p.status === 'error' && ' ✕'}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1">
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-square shrink-0 self-center"
          aria-label="Attach files or images"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
        >
          <PaperclipIcon />
        </button>

        <textarea
          ref={textRef}
          value={text}
          placeholder={placeholder}
          rows={1}
          className="flex-1 resize-none bg-transparent border-0 outline-none text-sm leading-relaxed min-h-[28px] max-h-[200px] py-0.5 px-1 self-center placeholder:text-base-content/40"
          onInput={(e) => setText(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />

        <button
          type="button"
          className="btn btn-ghost btn-sm btn-square shrink-0 self-center"
          aria-label="Send message"
          disabled={uploading > 0}
          onClick={submit}
        >
          <SendIcon className={`w-5 h-5 ${uploading > 0 ? 'opacity-50' : ''}`} />
        </button>
      </div>

      {menuOpen && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
          <div className="absolute bottom-full left-0 mb-1 z-30 w-40 rounded-xl border border-base-300 bg-base-100 shadow-lg p-1">
            {[
              { label: 'Files', input: filesInputRef },
              { label: 'Images', input: imagesInputRef },
              { label: 'Take Photo', input: cameraInputRef },
            ].map((it) => (
              <button
                key={it.label}
                type="button"
                className="btn btn-ghost btn-sm btn-block justify-start"
                onClick={() => {
                  setMenuOpen(false);
                  it.input.current && it.input.current.click();
                }}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}